import type { HeroState, UnitState } from '../../../shared/game/world-types.js';

const mix = (a: number, b: number, t: number) => a + (b - a) * t;
/** The windups that are a cast of their own (assessing and the like); an attack's windup is timed by its own clip and is left alone. */
const SCOUTING = new Set(['assess', 'opening']);

/**
 * A unit's state `alpha` of the way from `earlier` to `later` (two server snapshots), for what the animator reads off a cast.
 *
 * The server sends a snapshot fifteen times a second and the scene draws sixty, so a cast's `remaining` is a staircase: every pose and every
 * light that follows its progress would move fifteen times a second while everything else moves smoothly. This gives the animator `remaining` as
 * a straight line through the steps, so progress runs at the frame rate. It returns `later` itself when nothing is being cast (the usual case: no
 * copy is made), and a copy with only the cast's `remaining` moved otherwise; the server's own state is never touched.
 */
export function smoothCast<T extends UnitState>(earlier: T, later: T, alpha: number): T {
  if (earlier === later || alpha >= 1) return later;
  let result = later;
  const before = (earlier as Partial<HeroState>).class?.casting ?? null, after = (later as Partial<HeroState>).class?.casting ?? null;
  if (before || after) {
    const cast = after ?? before!;
    // A cast that began in this interval starts from its full time; one that ended in it runs down to nothing.
    const from = before && before.abilityId === cast.abilityId ? before.remaining : cast.duration, to = after ? after.remaining : 0;
    result = { ...result, class: { ...(later as unknown as HeroState).class, casting: { ...cast, remaining: mix(from, to, alpha) } } } as T;
  }
  const windupBefore = earlier.combat.windup, windupAfter = later.combat.windup;
  const windup = windupAfter ?? windupBefore;
  if (windup && SCOUTING.has(windup.kind)) {
    const from = windupBefore && windupBefore.kind === windup.kind ? windupBefore.remaining : windup.duration, to = windupAfter ? windupAfter.remaining : 0;
    result = { ...result, combat: { ...result.combat, windup: { ...windup, remaining: mix(from, to, alpha) } } } as T;
  }
  return result;
}
